import React, { useState, useEffect } from "react";
import {
  Modal,
  Box,
  Typography,
  IconButton,
  TextField,
  Button,
  CircularProgress,
  Alert,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { API_URLS } from "../../config/apiUrls";

interface SorteioProps {
  id: string;
  title: string;
  description: string;
  startDate: string;
  drawDate: string;
  displayEndDate: string;
  minNumber: number;
  maxNumber: number;
  ticketsPerUser: number;
  participationRule: string;
  isDeleted: boolean;
  createdAt: string;
  updatedAt: string;
  tickets: any[];
  participantsCount: number;
  availableNumbers: number;
  totalPrize: number;
  prizePercentage: number;
  role: "MASTER" | "PARTNER" | "ANALYST";
  prizeInfo: {
    basePrize: number;
    currentPrize: number;
    totalPrizeIncrease: number;
    approvedAdsCount: number;
    totalApprovedAdsValue: number;
    hasDynamicPrize: boolean;
    hasFixedPrize: boolean;
    hasPercentageOnly: boolean;
  };
}

interface SorteioEditModalProps {
  open: boolean;
  onClose: () => void;
  sorteio: SorteioProps;
  token: string;
}

const style = {
  position: "absolute" as "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: { xs: "90%", sm: 500 },
  bgcolor: "background.paper",
  borderRadius: 2,
  boxShadow: 24,
  p: 4,
  maxHeight: "90vh",
  overflowY: "auto",
};

const SorteioEditModal: React.FC<SorteioEditModalProps> = ({
  open,
  onClose,
  sorteio,
  token,
}) => {
  const [title, setTitle] = useState(sorteio.title);
  const [description, setDescription] = useState(sorteio.description);
  const [displayEndDate, setDisplayEndDate] = useState(
    new Date(sorteio.displayEndDate).toISOString().substring(0, 16)
  );
  // Mantemos o estado como string para evitar problemas de formatação com o TextField
  const [totalPrize, setTotalPrize] = useState(String(sorteio.totalPrize));
  const [prizePercentage, setPrizePercentage] = useState(
    String(sorteio.prizePercentage)
  );
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    setTitle(sorteio.title);
    setDescription(sorteio.description);
    setDisplayEndDate(
      new Date(sorteio.displayEndDate).toISOString().substring(0, 16)
    );
    // Assegura que o estado inicial é uma string para os campos numéricos
    setTotalPrize(String(sorteio.totalPrize));
    setPrizePercentage(String(sorteio.prizePercentage));
    setError(null);
    setSuccess(null);
  }, [sorteio, open]);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(null);

    // Função auxiliar para formatar o número, substituindo vírgula por ponto
    const formatNumber = (value: string) => {
      // Substitui vírgula por ponto, se existir
      const formattedValue = value.replace(",", ".");
      // Converte a string para um número de ponto flutuante
      return parseFloat(formattedValue);
    };

    const updateData: {
      title: string;
      description: string;
      displayEndDate: string;
      totalPrize?: number;
      prizePercentage?: number;
    } = {
      title,
      description,
      displayEndDate: new Date(displayEndDate).toISOString(),
    };

    // Adiciona as propriedades de prêmio apenas se forem aplicáveis
    if (sorteio.prizeInfo.hasDynamicPrize || sorteio.prizeInfo.hasFixedPrize) {
      updateData.totalPrize = formatNumber(totalPrize);
    }

    if (
      sorteio.prizeInfo.hasDynamicPrize ||
      sorteio.prizeInfo.hasPercentageOnly
    ) {
      updateData.prizePercentage = formatNumber(prizePercentage);
    }

    try {
      const response = await fetch(`${API_URLS.sorteios}/${sorteio.id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(updateData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Erro ao atualizar sorteio.");
      }

      setSuccess("Sorteio atualizado com sucesso!");
      onClose();
    } catch (err) {
      if (err instanceof Error) {
        setError(err.message);
      } else {
        setError("Ocorreu um erro desconhecido ao atualizar o sorteio.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="edit-sorteio-modal-title"
      aria-describedby="edit-sorteio-modal-description"
    >
      <Box sx={style} component="form" onSubmit={handleSubmit}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          <Typography id="edit-sorteio-modal-title" variant="h6" component="h2">
            Editar Sorteio: {sorteio.title}
          </Typography>
          <IconButton onClick={onClose} aria-label="fechar">
            <CloseIcon />
          </IconButton>
        </Box>

        <TextField
          label="Título"
          fullWidth
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          margin="normal"
          required
        />
        <TextField
          label="Descrição"
          fullWidth
          multiline
          rows={4}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          margin="normal"
          required
        />
        <TextField
          label="Data Final"
          type="datetime-local"
          fullWidth
          value={displayEndDate}
          onChange={(e) => setDisplayEndDate(e.target.value)}
          margin="normal"
          InputLabelProps={{ shrink: true }}
          required
        />

        {sorteio.prizeInfo.hasDynamicPrize && (
          <>
            <TextField
              label="Valor do Prêmio Total"
              type="text" // Mudado para text para controlar a formatação
              fullWidth
              value={totalPrize}
              onChange={(e) => setTotalPrize(e.target.value)}
              margin="normal"
              required
            />
            <TextField
              label="Porcentagem do Prêmio"
              type="text" // Mudado para text
              fullWidth
              value={prizePercentage}
              onChange={(e) => setPrizePercentage(e.target.value)}
              margin="normal"
              required
            />
          </>
        )}

        {sorteio.prizeInfo.hasFixedPrize && (
          <TextField
            label="Valor do Prêmio Total"
            type="text"
            fullWidth
            value={totalPrize}
            onChange={(e) => setTotalPrize(e.target.value)}
            margin="normal"
            required
          />
        )}

        {sorteio.prizeInfo.hasPercentageOnly && (
          <TextField
            label="Porcentagem do Prêmio"
            type="text"
            fullWidth
            value={prizePercentage}
            onChange={(e) => setPrizePercentage(e.target.value)}
            margin="normal"
            required
          />
        )}

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mt: 2 }}>
            {success}
          </Alert>
        )}

        <Box
          sx={{ mt: 3, display: "flex", justifyContent: "flex-end", gap: 2 }}
        >
          <Button variant="outlined" onClick={onClose} disabled={loading}>
            Cancelar
          </Button>
          <Button type="submit" variant="contained" disabled={loading}>
            {loading ? <CircularProgress size={24} /> : "Salvar Alterações"}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default SorteioEditModal;
